/**
 * The play window in words: the clock the hand and the cards button share,
 * and the lines that say when cards can be played. Cards play from the day's
 * unlock to unlock + 45 minutes (`PLAY_WINDOW_MS`, backend
 * `modules/cards/services/playWindow.ts`).
 */
import type { PlayWindow } from "../types/handTypes";

export interface WindowClock {
  /** Open by the server's read and not yet closed by the client's clock. */
  open: boolean;
  /** ms left while open, else 0. */
  left: number;
  /** epoch ms, NaN when unknown */
  closesAt: number;
  /** epoch ms, NaN when unknown */
  nextOpensAt: number;
  /** The window opened or closed since the last read: read /my-cards again. */
  turnedOver: boolean;
}

export function windowClock(win: PlayWindow | undefined, now: number): WindowClock {
  const closesAt = win?.closesAt ? Date.parse(win.closesAt) : NaN;
  const nextOpensAt = win?.nextOpensAt ? Date.parse(win.nextOpensAt) : NaN;
  const left = win?.open && Number.isFinite(closesAt) ? Math.max(0, closesAt - now) : 0;
  const open = Boolean(win?.open) && left > 0;
  const turnedOver =
    (Boolean(win?.open) && left <= 0) || (!win?.open && Number.isFinite(nextOpensAt) && now >= nextOpensAt);
  return { open, left, closesAt, nextOpensAt, turnedOver };
}

/** "07:50" in the viewer's own time. */
export function clockTime(ms: number): string {
  return new Date(ms).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}

function dayStart(ms: number): number {
  const d = new Date(ms);
  return new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
}

/** "at 07:50", "tomorrow at 07:50" or "on friday at 07:50". */
export function atTime(ms: number, now: number): string {
  const days = Math.round((dayStart(ms) - dayStart(now)) / 86_400_000);
  const time = clockTime(ms);
  if (days <= 0) return `at ${time}`;
  if (days === 1) return `tomorrow at ${time}`;
  return `on ${new Date(ms).toLocaleDateString([], { weekday: "long" }).toLowerCase()} at ${time}`;
}

/** "12:04", minutes and seconds. */
export function countdown(ms: number): string {
  const mm = Math.floor(ms / 60000);
  const ss = String(Math.floor((ms % 60000) / 1000)).padStart(2, "0");
  return `${mm}:${ss}`;
}

export const WINDOW_RULE = "you can play cards from the unlock until 45 minutes after it.";

/** The hand's header line. */
export function windowLine(clock: WindowClock, now: number): string {
  if (clock.open) return `you can play cards now, until 45 minutes after the unlock. ${countdown(clock.left)} left.`;
  if (Number.isFinite(clock.nextOpensAt) && clock.nextOpensAt > now) {
    return `${WINDOW_RULE} next unlock ${atTime(clock.nextOpensAt, now)}.`;
  }
  return WINDOW_RULE;
}

/** When a new card can be played, for the new-card modal. */
export function newCardWhenLine(clock: WindowClock, now: number): string {
  if (clock.open && Number.isFinite(clock.closesAt)) {
    return `keep it and you can play it now, until ${clockTime(clock.closesAt)}.`;
  }
  if (Number.isFinite(clock.nextOpensAt) && clock.nextOpensAt > now) {
    return `you can play it from the unlock ${atTime(clock.nextOpensAt, now)} until 45 minutes after.`;
  }
  return "you can play it from the unlock until 45 minutes after it.";
}
